/**
 * Seeded, deterministic PRNG. [P1-rng]
 *
 * The engine has no clock and no ambient randomness. The generator's entire state is a single 32-bit
 * integer carried inside `GameState`, which is what makes a game reproducible from `(seed, actions[])`.
 * That reproducibility is not a nicety — it is what lets the server persist a game as its action log,
 * lets tests replay a fixture exactly, and lets the client verify it agrees with the server.
 *
 * `Math.random` is banned in this package by an ESLint rule.
 */
export function createRng(seed) {
    // Avoid the degenerate all-zero state.
    const s = (seed | 0) === 0 ? 0x9e3779b9 : seed | 0;
    return { state: s >>> 0 };
}
/**
 * mulberry32 — small, fast, and good enough for shuffling a 72-tile deck. Returns the next state
 * alongside the value so callers cannot accidentally reuse a consumed generator.
 */
export function next(rng) {
    let t = (rng.state + 0x6d2b79f5) >>> 0;
    const nextState = t;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    return { rng: { state: nextState }, value };
}
/** Uniform integer in [0, bound). `bound` must be a positive integer. */
export function nextInt(rng, bound) {
    if (!Number.isInteger(bound) || bound <= 0) {
        throw new RangeError(`nextInt bound must be a positive integer, got ${bound}`);
    }
    const r = next(rng);
    return { rng: r.rng, value: Math.floor(r.value * bound) };
}
/**
 * Fisher-Yates, returning a new array. The input is never mutated — several call sites shuffle a deck
 * derived from shared tile data, and mutating it would corrupt every subsequent game in the process.
 */
export function shuffle(rng, items) {
    const out = items.slice();
    let r = rng;
    for (let i = out.length - 1; i > 0; i--) {
        const step = nextInt(r, i + 1);
        r = step.rng;
        const j = step.value;
        const a = out[i];
        const b = out[j];
        out[i] = b;
        out[j] = a;
    }
    return { rng: r, value: out };
}
//# sourceMappingURL=rng.js.map